import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ArrowUpDown, ArrowUp, ArrowDown, Trophy, Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PitchRetentionCell } from '@/components/metrics/pitch/PitchRetentionCell';
import type { TestPitchMetric } from '@/types/pitch';

export interface VideoPerformance {
  id: string;
  name: string;
  thumbnail: string;
  isMain?: boolean;
  introNumber?: number;
  isWinner?: boolean;
  views: number;
  uniqueViews?: number;
  plays: number;
  uniquePlays: number;
  playRate?: number;
  engagement: number;
  /** Pitches do vídeo com a retenção de cada um (raiz de MetricService.execute). */
  pitches?: TestPitchMetric[];
  pitchVideoId?: string | null;
  videoDuration?: number | null;
  // Métricas de rastreamento (opcionais — populadas quando há integração de conversão)
  conversions?: number;
  revenue?: number;
  averageTicket?: number;
}

export interface MetricColumn {
  id: string;
  label: string;
  visible: boolean;
}

interface HookTrackPerformanceTableProps {
  videos: VideoPerformance[];
  columns?: MetricColumn[];
  showTotals?: boolean;
  /** Refaz o fetch após configurar o pitch. */
  onPitchSaved?: () => unknown;
  /** Posição do pitch (0 = 1º) que preenche a coluna "Retenção no Pitch". */
  pitchPosition?: number;
  /** Mostra o nome do pitch de cada vídeo na célula (seletor com 2+ opções). */
  showPitchName?: boolean;
}

type SortField = 'name' | 'views' | 'uniqueViews' | 'plays' | 'uniquePlays' | 'playRate' | 'engagement' | 'pitchRetention' | 'conversions' | 'revenue' | 'averageTicket';

export function HookTrackPerformanceTable({ videos, columns, showTotals = true, onPitchSaved, pitchPosition = 0, showPitchName = false }: HookTrackPerformanceTableProps) {
  useLanguage();
  const [sortField, setSortField] = useState<SortField>('views');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedVideos = useMemo(() => {
    // Sem pitch na posição ordena como -1: fica no fim da ordem decrescente.
    const sortValue = (video: VideoPerformance) =>
      sortField === 'pitchRetention' ? video.pitches?.[pitchPosition]?.retentionPct ?? -1 : video[sortField] ?? 0;
    return [...videos].sort((a, b) => {
      const aValue = sortValue(a);
      const bValue = sortValue(b);
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }
      return sortDirection === 'asc' ? (aValue as number) - (bValue as number) : (bValue as number) - (aValue as number);
    });
  }, [videos, sortField, sortDirection, pitchPosition]);

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <ArrowUpDown className="w-3.5 h-3.5 text-[#AEB8C9]" />;
    return sortDirection === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#4B7BFF]" /> : <ArrowDown className="w-3.5 h-3.5 text-[#4B7BFF]" />;
  };

  const isColumnVisible = (columnId: string) => {
    if (!columns) return true;
    const column = columns.find(c => c.id === columnId);
    return column ? column.visible : true;
  };

  const formatNumber = (num: number) => {
    const safeNum = Number(num) || 0;
    return safeNum >= 1000 ? safeToFixed(safeNum / 1000, 1) + 'k' : safeNum.toLocaleString(getInterfaceLanguage());
  };

  const safeToFixed = (value: any, decimals: number = 1): string => {
    const num = Number(value);
    return isNaN(num) ? '0.0' : num.toFixed(decimals);
  };

  const formatCurrency = (value: number) =>
    (Number(value) || 0).toLocaleString(getInterfaceLanguage(), { style: 'currency', currency: 'BRL' });

  const getBadge = (video: VideoPerformance) => {
    if (video.isWinner) return (
      <Badge className="ml-2 text-[12px] font-medium px-2.5 py-1 bg-primary text-white border-primary rounded-full flex items-center gap-1">
        <Trophy className="w-3 h-3" />
        {translateInterfaceText("Vencendo")}</Badge>
    );
    if (video.isMain) return (
      <Badge variant="outline" className="ml-2 text-[12px] font-medium px-2.5 py-1 bg-primary/10 text-primary border-primary/20 rounded-full flex items-center gap-1">
        <Star className="w-3 h-3" />
        {translateInterfaceText("Principal")}</Badge>
    );
    if (video.introNumber) return (
      <Badge variant="outline" className="ml-2 text-[12px] font-medium px-2.5 py-1 bg-muted text-muted-foreground border-border rounded-full">
        {translateInterfaceText("Intro #")}{video.introNumber}
      </Badge>
    );
    return null;
  };

  const totals = videos.reduce((acc, v) => ({
    views: acc.views + v.views,
    uniqueViews: acc.uniqueViews + (v.uniqueViews || 0),
    plays: acc.plays + v.plays,
    uniquePlays: acc.uniquePlays + v.uniquePlays,
    conversions: acc.conversions + (v.conversions || 0),
    revenue: acc.revenue + (v.revenue || 0),
  }), { views: 0, uniqueViews: 0, plays: 0, uniquePlays: 0, conversions: 0, revenue: 0 });

  const avgPlayRate = videos.length > 0 ? parseFloat((videos.reduce((sum, v) => sum + (v.playRate || 0), 0) / videos.length).toFixed(2)) : 0;
  const avgEngagement = videos.length > 0 ? parseFloat((videos.reduce((sum, v) => sum + v.engagement, 0) / videos.length).toFixed(2)) : 0;
  // Ticket médio do teste = receita total / conversões totais (não a média das intros)
  const avgTicketTotal = totals.conversions > 0 ? totals.revenue / totals.conversions : 0;

  const headerClass = "bg-[#F7F9FF] text-[13px] font-semibold text-[#5A6475] cursor-pointer group hover:bg-[#EDF1FF] transition-colors whitespace-nowrap";
  const headerStyle = { borderBottom: '1px solid #EDF1FF', padding: '14px 18px', height: '56px' };

  return (
    <div className="overflow-hidden rounded-[14px]" style={{ background: '#FFFFFF', border: '1px solid #EDF1FF', boxShadow: '0px 12px 28px rgba(0, 0, 0, 0.04)' }}>
      <div className="overflow-x-auto hidden lg:block">
        <Table>
          <TableHeader>
            <TableRow className="border-0">
              <TableHead className={cn(headerClass, "min-w-[280px]")} style={headerStyle} onClick={() => handleSort('name')}><div className="flex items-center gap-2">{translateInterfaceText("Vídeo")}{" "}{getSortIcon('name')}</div></TableHead>
              {isColumnVisible('views') && <TableHead className={headerClass} style={headerStyle} onClick={() => handleSort('views')}><div className="flex items-center gap-2">{translateInterfaceText("Visualizações")}{" "}{getSortIcon('views')}</div></TableHead>}
              {isColumnVisible('uniqueViews') && <TableHead className={headerClass} style={headerStyle} onClick={() => handleSort('uniqueViews')}><div className="flex items-center gap-2">{translateInterfaceText("Vis. Únicas")}{" "}{getSortIcon('uniqueViews')}</div></TableHead>}
              {isColumnVisible('plays') && <TableHead className={headerClass} style={headerStyle} onClick={() => handleSort('plays')}><div className="flex items-center gap-2">{translateInterfaceText("Plays")}{" "}{getSortIcon('plays')}</div></TableHead>}
              {isColumnVisible('uniquePlays') && <TableHead className={headerClass} style={headerStyle} onClick={() => handleSort('uniquePlays')}><div className="flex items-center gap-2">{translateInterfaceText("Plays Únicos")}{" "}{getSortIcon('uniquePlays')}</div></TableHead>}
              {isColumnVisible('playRate') && <TableHead className={headerClass} style={headerStyle} onClick={() => handleSort('playRate')}><div className="flex items-center gap-2">{translateInterfaceText("Play Rate")}{" "}{getSortIcon('playRate')}</div></TableHead>}
              {isColumnVisible('engagement') && <TableHead className={headerClass} style={headerStyle} onClick={() => handleSort('engagement')}><div className="flex items-center gap-2">{translateInterfaceText("Engajamento")}{" "}{getSortIcon('engagement')}</div></TableHead>}
              {isColumnVisible('pitchRetention') && <TableHead className={headerClass} style={headerStyle} onClick={() => handleSort('pitchRetention')}><div className="flex items-center gap-2">{translateInterfaceText("Retenção no Pitch")}{" "}{getSortIcon('pitchRetention')}</div></TableHead>}
              {isColumnVisible('conversions') && <TableHead className={headerClass} style={headerStyle} onClick={() => handleSort('conversions')}><div className="flex items-center gap-2">{translateInterfaceText("Conversões")}{" "}{getSortIcon('conversions')}</div></TableHead>}
              {isColumnVisible('revenue') && <TableHead className={headerClass} style={headerStyle} onClick={() => handleSort('revenue')}><div className="flex items-center gap-2">{translateInterfaceText("Receita")}{" "}{getSortIcon('revenue')}</div></TableHead>}
              {isColumnVisible('averageTicket') && <TableHead className={headerClass} style={headerStyle} onClick={() => handleSort('averageTicket')}><div className="flex items-center gap-2">{translateInterfaceText("Ticket Médio")}{" "}{getSortIcon('averageTicket')}</div></TableHead>}

            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedVideos.map((video, index) => (
              <TableRow key={video.id} className={cn(index % 2 === 0 ? 'bg-white' : 'bg-[#FAFBFF]', 'hover:bg-[#F3F6FF] transition-all duration-150 border-b border-[#EDF1FF]')} style={{ height: '56px' }}>
                <TableCell className="py-3 px-4"><div className="flex items-center gap-3"><div className="w-16 h-9 rounded-lg overflow-hidden bg-muted flex-shrink-0"><img src={video.thumbnail} alt={video.name} className="w-full h-full object-cover" /></div><div className="flex items-center"><span className="text-[14px] font-semibold text-[#2C3345] truncate max-w-[180px]">{video.name}</span>{getBadge(video)}</div></div></TableCell>
                {isColumnVisible('views') && <TableCell className="py-3 px-4 text-[14px] text-[#2C3345]">{formatNumber(video.views)}</TableCell>}
                {isColumnVisible('uniqueViews') && <TableCell className="py-3 px-4 text-[14px] text-[#2C3345]">{formatNumber(video.uniqueViews || 0)}</TableCell>}
                {isColumnVisible('plays') && <TableCell className="py-3 px-4 text-[14px] text-[#2C3345]">{formatNumber(video.plays)}</TableCell>}
                {isColumnVisible('uniquePlays') && <TableCell className="py-3 px-4 text-[14px] text-[#2C3345]">{formatNumber(video.uniquePlays)}</TableCell>}
                {isColumnVisible('playRate') && <TableCell className="py-3 px-4"><span className="inline-flex items-center px-2 py-0.5 rounded text-[13px] font-medium" style={{ color: '#5A6475', background: 'transparent' }}>{(video.playRate || 0)}%</span></TableCell>}
                {isColumnVisible('engagement') && <TableCell className="py-3 px-4"><span className="inline-flex items-center px-2 py-0.5 rounded text-[13px] font-medium" style={{ color: '#5A6475', background: 'transparent' }}>{video.engagement}%</span></TableCell>}
                {isColumnVisible('pitchRetention') && <TableCell className="py-3 px-4"><PitchRetentionCell pitch={video.pitches?.[pitchPosition] ?? null} videoPitches={video.pitches ?? []} pitchVideoId={video.pitchVideoId} durationInSeconds={video.videoDuration} showName={showPitchName} onSaved={onPitchSaved} /></TableCell>}
                {isColumnVisible('conversions') && <TableCell className="py-3 px-4 text-[14px] text-[#2C3345]">{formatNumber(video.conversions || 0)}</TableCell>}
                {isColumnVisible('revenue') && <TableCell className="py-3 px-4 text-[14px] text-[#2C3345]">{formatCurrency(video.revenue || 0)}</TableCell>}
                {isColumnVisible('averageTicket') && <TableCell className="py-3 px-4 text-[14px] text-[#2C3345]">{formatCurrency(video.averageTicket || 0)}</TableCell>}
              </TableRow>
            ))}
            {showTotals && (
              <TableRow className="bg-[#F7F9FF] border-t-2 border-[#EDF1FF] font-semibold" style={{ height: '56px' }}>
                <TableCell className="py-3 px-4 text-[14px] text-[#2C3345] font-bold">{translateInterfaceText("Total do teste")}</TableCell>
                {isColumnVisible('views') && <TableCell className="py-3 px-4 text-[14px] text-[#2C3345] font-bold">{formatNumber(totals.views)}</TableCell>}
                {isColumnVisible('uniqueViews') && <TableCell className="py-3 px-4 text-[14px] text-[#2C3345] font-bold">{formatNumber(totals.uniqueViews)}</TableCell>}
                {isColumnVisible('plays') && <TableCell className="py-3 px-4 text-[14px] text-[#2C3345] font-bold">{formatNumber(totals.plays)}</TableCell>}
                {isColumnVisible('uniquePlays') && <TableCell className="py-3 px-4 text-[14px] text-[#2C3345] font-bold">{formatNumber(totals.uniquePlays)}</TableCell>}
                {isColumnVisible('playRate') && <TableCell className="py-3 px-4"><span className="inline-flex items-center px-2 py-0.5 rounded text-[13px] font-bold" style={{ color: '#5A6475', background: 'transparent' }}>{avgPlayRate}%</span></TableCell>}
                {isColumnVisible('engagement') && <TableCell className="py-3 px-4"><span className="inline-flex items-center px-2 py-0.5 rounded text-[13px] font-bold" style={{ color: '#5A6475', background: 'transparent' }}>{avgEngagement}%</span></TableCell>}
                {/* Sem total: média de percentuais entre vídeos com bases de plays diferentes não tem significado. */}
                {isColumnVisible('pitchRetention') && <TableCell className="py-3 px-4" />}
                {isColumnVisible('conversions') && <TableCell className="py-3 px-4 text-[14px] text-[#2C3345] font-bold">{formatNumber(totals.conversions)}</TableCell>}
                {isColumnVisible('revenue') && <TableCell className="py-3 px-4 text-[14px] text-[#2C3345] font-bold">{formatCurrency(totals.revenue)}</TableCell>}
                {isColumnVisible('averageTicket') && <TableCell className="py-3 px-4 text-[14px] text-[#2C3345] font-bold">{formatCurrency(avgTicketTotal)}</TableCell>}

              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4 p-4">
        {videos.map((video) => (
          <Card key={video.id} className="p-4">
            <div className="flex items-start gap-3 mb-4">
              <img src={video.thumbnail} alt={video.name} className="w-24 h-16 object-cover rounded flex-shrink-0" />
              <div className="flex-1 min-w-0"><h3 className="font-medium text-foreground truncate mb-1">{video.name}</h3><div className="flex flex-wrap items-center gap-2">{getBadge(video)}</div></div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><div className="text-muted-foreground">{translateInterfaceText("Visualizações")}</div><div className="font-medium">{formatNumber(video.views)}</div></div>
              <div><div className="text-muted-foreground">{translateInterfaceText("Plays")}</div><div className="font-medium">{formatNumber(video.plays)}</div></div>
              <div><div className="text-muted-foreground">{translateInterfaceText("Play Rate")}</div><div className="font-medium">{(video.playRate || 0)}%</div></div>
              <div><div className="text-muted-foreground">{translateInterfaceText("Engajamento")}</div><div className="font-medium">{video.engagement}%</div></div>
              {video.conversions !== undefined && <div><div className="text-muted-foreground">{translateInterfaceText("Conversões")}</div><div className="font-medium">{formatNumber(video.conversions || 0)}</div></div>}
              {video.revenue !== undefined && <div><div className="text-muted-foreground">{translateInterfaceText("Receita")}</div><div className="font-medium">{formatCurrency(video.revenue || 0)}</div></div>}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
